
import { Folder } from "@/models/tasks/folder";
import { FolderUser } from "@/models/tasks/folder-user";
import { TaskService } from "@/services/task-service";
import { TasksState } from "@/utils/tasks-state";
import Vue from "vue";
import { Task } from "../../models/tasks/task";
import AppTaskEditModal from "./TaskEditModal.vue";
import AppTaskItem from "./TaskItem.vue";
import AppTaskListWrapper from "./TaskListWrapper.vue";
import { RepeatedTask } from "@/models/tasks/repeated-task";
import AppRepeatedTaskEditModal from "./RepeatedTaskEditModal.vue";

export default Vue.extend({
	data() {
		return {
			editModal: false,
			modalTask: new Task(),
			taskId: 0,
			tasksBeforeDragged: [] as Task[],
			tasksAfterDragged: [] as Task[],
			isDraggingInsideEl: false,
			repeatedTaskEdit: null as null | RepeatedTask
		};
	},
	props: {
		state: {
			type: Object as () => TasksState,
			required: true
		},
	},
	computed: {
		folderUser():FolderUser {
			return this.state.openedFolder as FolderUser;
		},
		canEdit(): boolean {
			return this.folderUser.IsOwner;
		},
		allTasks(): Task[] {
			return [...this.state.pendingTasks, ...this.state.completedTasks].sort((a, b) => a.OrderNumber - b.OrderNumber);
		},
	},
	watch: {
		allTasks: {
			immediate: true,
			handler() {
				if (!this.state.draggedTask) {
					this.tasksBeforeDragged = [...this.allTasks];
					this.tasksAfterDragged = [];
				}
			}
		},
		"state.draggedTask"() {
			if (!this.state.draggedTask) {
				this.isDraggingInsideEl = false;
			}
		}
	},
	methods: {
		addItem() {
			this.modalTask = new Task();
			this.modalTask.FolderID = this.folderUser.FolderID;
			this.editModal = true;
		},
		addRepeated(){
			this.repeatedTaskEdit = new RepeatedTask();
			this.repeatedTaskEdit.FolderID = this.folderUser.FolderID;
		},
		editTask(task: Task) {
			this.modalTask = new Task(task.getJSON());
			this.editModal = true;
		},
		async removeTask(task: Task) {
			let confirm = await this.$wf.confirm(this.$ct("confirm-delete-this-task",), this.$ct("common.yes"), this.$ct("common.no"), this.$ct("common.warning"), "warning");
			if (!confirm) return;
			this.editModal = false;
			await TaskService.deleteTask(task);
		},
		toggleCompleted() {
			if (!this.state.showCompletedTasks) {
				this.state.fetchCompletedTasks();
			} else {
				this.state.hideCompletedTasks();
			}
		},
		refresh() {
			this.$emit("refresh");
		},
		onMouseMove(event:MouseEvent) {
			if (!this.state.draggedTask) return;
			this.taskOrderPreview(event.pageX, event.pageY);
		},
		onTouchMove(event:TouchEvent) {
			if (!this.state.draggedTask) return;
			let touch = event.touches.item(0) as Touch;
			this.taskOrderPreview(touch.pageX, touch.pageY);
		},
		isPositionInEl(x:number, y:number):boolean {
			let rect = this.$el.getBoundingClientRect();
			if (rect.top > y || rect.bottom < y) {
				return false;
			}
			if (rect.left > x || rect.right < x) {
				return false;
			}
			return true;
		},
		resetTaskOrderPreview() {
			this.isDraggingInsideEl = false;
			if (this.tasksAfterDragged.length == 0) return;
			this.tasksAfterDragged = [];
			this.tasksBeforeDragged = [...this.allTasks];
		},
		// eslint-disable-next-line max-lines-per-function
		taskOrderPreview(x:number, y:number) {
			if (!this.state.draggedTask) return;
			if (!this.isPositionInEl(x, y)) {
				this.resetTaskOrderPreview();
				return;
			}
			this.isDraggingInsideEl = true;
			let beforeTasks = (this.$refs.beforeTasks || []) as Vue[];
			let afterTasks = (this.$refs.afterTasks || []) as Vue[];
			let allTasksEls = [...beforeTasks, ...afterTasks.reverse()];
			let listRootEl = this.$refs.listRoot as HTMLElement;
			let heights = allTasksEls.map(el => {
				let rect = el.$el.getBoundingClientRect();
				return rect.height;
			});
			let index = this.allTasks.indexOf(this.state.draggedTask);
			heights[index] = 0;
			let totalHeight = listRootEl.getBoundingClientRect().top;
			if (totalHeight > y) {
				this.tasksBeforeDragged = [];
				this.tasksAfterDragged = [...this.allTasks];
			}
			for (let i = 0; i < heights.length; i++) {
				totalHeight += heights[i];
				if (totalHeight > y) {
					let beforeTasks = [];
					let afterTasks = [];
					for (let c = 0; c < i; c++) {
						beforeTasks.push(this.allTasks[c]);
					}
					for (let c = i; c < this.allTasks.length; c++) {
						afterTasks.push(this.allTasks[c]);
					}
					this.tasksBeforeDragged = beforeTasks;
					this.tasksAfterDragged = afterTasks;
					return;
				}
			}
			this.tasksBeforeDragged = [...this.allTasks];
			this.tasksAfterDragged = [];
		},
		async onMouseUp(event:MouseEvent) {
			if (this.isPositionInEl(event.pageX, event.pageY)) {
				await this.onTaskDropped();
			}
			this.resetTaskOrderPreview();
		},
		async onTouchEnd(event:TouchEvent) {
			let touch = event.changedTouches.item(0) as Touch;
			if (this.isPositionInEl(touch.pageX, touch.pageY)) {
				await this.onTaskDropped();
			}
			this.resetTaskOrderPreview();
		},
		async onTaskDropped() {
			if (!this.state.draggedTask) return;
			let beforeTasks = this.tasksBeforeDragged.filter(t => t != this.state.draggedTask);
			let afterTasks = this.tasksAfterDragged.filter(t => t != this.state.draggedTask);
			let allTasks = [...beforeTasks, this.state.draggedTask, ...afterTasks];
			await TaskService.sortTasks(this.folderUser.Folder as Folder, allTasks);
		}
	},
	created() {
		window.addEventListener("mousemove", this.onMouseMove);
		window.addEventListener("touchmove", this.onTouchMove);
		window.addEventListener("mouseup", this.onMouseUp);
		window.addEventListener("touchend", this.onTouchEnd);
	},
	destroyed() {
		window.removeEventListener("mousemove", this.onMouseMove);
		window.removeEventListener("touchmove", this.onTouchMove);
		window.removeEventListener("mouseup", this.onMouseUp);
		window.removeEventListener("touchend", this.onTouchEnd);
	},
	components: {
		AppTaskEditModal,
		AppTaskItem,
		AppTaskListWrapper,
		AppRepeatedTaskEditModal
	},
	i18nEx: {
		componentPrefix: "components.tasks"
	}
});
